import React, { useContext, useMemo } from 'react';
import _ from 'lodash';
import DimensionsInput from './DimensionsInput';
import V5GadgetFormContext from '../../../contexts/V5GadgetFormContext';
import useFieldDefinitions from '../../../hooks/useFieldDefinitions';
import useDataTypesFromSeriesAndMetricListItems from '../../../hooks/useDataTypesFromSeriesAndMetricListItems';

const DimensionsInputContainer = () => {
  const {
    dimensionA,
    setDimensionA,
    secondaryDimensionA,
    setSecondaryDimensionA,
    isSecondaryDimensionAAvailable,
    dimensionB,
    setDimensionB,
    series,
    isMultipleDimensionChart,
  } = useContext(V5GadgetFormContext);
  const usedDataTypes = useDataTypesFromSeriesAndMetricListItems(series);
  const { fieldDefinitions } = useFieldDefinitions(usedDataTypes);

  const availableDimensions = useMemo(() => {
    const baseDimensions = _.uniqBy(fieldDefinitions, (field) => field.field)
      .filter((d) => d.type !== 'date')
      .map(
        (f) =>
          ({
            field: f.field,
            fieldType: f.type,
            isGroupByDayOfWeek: false,
          }) as Dimension,
      );

    const hasDayOfWeek = baseDimensions.some((d) => d.field === 'dayOfWeek');
    if (!hasDayOfWeek) {
      baseDimensions.push({
        field: 'dayOfWeek',
        isGroupByDayOfWeek: true,
        isGroupByMonth: false,
      });
    }

    const hasMonth = baseDimensions.some((d) => d.field === 'month');
    if (!hasMonth) {
      baseDimensions.push({
        field: 'month',
        isGroupByMonth: true,
        isGroupByDayOfWeek: false,
      });
    }

    baseDimensions.push({
      field: 'date',
      fieldType: 'date',
      isGroupByDayOfWeek: false,
      isGroupByMonth: false,
    });

    return _.sortBy(baseDimensions, 'field');
  }, [fieldDefinitions]);

  // The secondary row field is a plain categorical term (sent as subGroupBy),
  // so the synthetic dayOfWeek / month / date options don't apply.
  const availableSecondaryDimensions = useMemo(
    () =>
      availableDimensions.filter(
        (d) =>
          (d.fieldType === 'text' || d.fieldType === 'boolean') &&
          d.field !== 'id' &&
          (!dimensionA || d.field !== dimensionA.field),
      ),
    [availableDimensions, dimensionA],
  );

  return (
    <DimensionsInput
      dimensionA={dimensionA}
      setDimensionA={setDimensionA}
      secondaryDimensionA={secondaryDimensionA}
      setSecondaryDimensionA={setSecondaryDimensionA}
      isSecondaryDimensionAAvailable={isSecondaryDimensionAAvailable}
      availableSecondaryDimensions={availableSecondaryDimensions}
      dimensionB={dimensionB}
      setDimensionB={setDimensionB}
      availableDimensions={availableDimensions}
      isMultipleDimensionChart={isMultipleDimensionChart}
    />
  );
};

export default DimensionsInputContainer;
